/**
 * Shared utilities for working with MediaInfo.js to extract video metadata
 */

// Module-level variables to maintain singleton instances
let mediaInfoModule = null;
let mediaInfoInstance = null;

export const isVideoFile = (file) => {
  const fileExtension = file.name.split('.').pop().toLowerCase();
  return file.type.startsWith('video/') || ['mov', 'avi', 'mp4', 'aivu'].includes(fileExtension);
};

/**
 * Initialize MediaInfo.js
 * @returns {Promise<Function>} A function that can be used to analyze media files
 */
export const initMediaInfo = async () => {
  if (!mediaInfoModule) {
    const { default: MediaInfo } = await import('mediainfo.js');
    mediaInfoModule = await MediaInfo({
      format: 'object',
      locateFile: (path) => {
        if (path.endsWith('.wasm')) {
          return `/mediainfo/${path}`;
        }
        return path;
      }
    });
  }
  
  if (!mediaInfoInstance) {
    mediaInfoInstance = mediaInfoModule.analyzeData.bind(mediaInfoModule);
  }
  
  return mediaInfoInstance;
};

/**
 * Extract metadata from a video file using MediaInfo.js
 * @param {File} file - The video file to analyze
 * @returns {Promise<Object>} Object containing extracted metadata
 */
export const extractVideoMetadata = async (file) => {
  const analyzeData = await initMediaInfo();
  
  const getSize = () => file.size;
  const readChunk = async (size, offset) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = (e) => {
        resolve(new Uint8Array(e.target.result));
      };
      reader.onerror = (e) => {
        console.warn('Error reading video chunk:', e);
        reject(e);
      };
      reader.readAsArrayBuffer(file.slice(offset, offset + size));
    });
  };

  const result = await analyzeData(getSize, readChunk);
  
  if (!result?.media?.track) {
    console.warn('No tracks found in video file');
    return {};
  }

  const videoTrack = result.media.track.find(track => track['@type'] === 'Video');
  const generalTrack = result.media.track.find(track => track['@type'] === 'General');

  console.debug('Video track:', videoTrack);
  console.debug('General track:', generalTrack);

  if (!videoTrack || !generalTrack) {
    console.warn('No video or general track found');
    return {};
  }

  // Parse video metadata
  const metadataFields = {
    duration: { track: generalTrack, field: 'Duration', parse: v => Math.ceil(parseFloat(v || 0)), validate: v => v > 0 },
    file_framerate: { track: videoTrack, field: 'FrameRate', parse: v => parseFloat(v || 0), validate: v => v > 0 },
    file_format: { track: videoTrack, field: 'Format', parse: v => v, validate: v => !!v },
    file_codec_id: { track: videoTrack, field: 'CodecID', parse: v => v, validate: v => !!v },
    file_bit_depth: { track: videoTrack, field: 'BitDepth', parse: v => parseInt(v || 0), validate: v => v > 0 },
    file_bit_rate: { track: videoTrack, field: 'BitRate', parse: v => parseInt(v || 0), validate: v => v > 0 },
    file_encoding_date: { track: generalTrack, field: 'Encoded_Date', parse: v => v, validate: v => !!v }
  };
  const metadata = Object.fromEntries(
    Object.entries(metadataFields)
      .map(([key, { track, field, parse, validate }]) => {
        const value = parse(track[field]);
        return validate(value) ? [key, value] : null;
      })
      .filter(Boolean)
  );
  // Special case for resolution
  if (videoTrack['Width'] && videoTrack['Height']) {
    metadata['file_resolution'] = `${videoTrack['Width']}x${videoTrack['Height']}`;
  }

  console.debug('Extracted metadata:', metadata);
  return metadata;
};
